import { ref, watch } from 'vue';

/**
 * Composable for managing the Add Identifier Dialog state and behavior
 */
export function useAddIdentifierDialog() {
    // Local state
    const localIdentifier = ref('');
    const localTexts = ref<Record<string, string>>({});
    const isVisible = ref(false);

    // Methods
    function show() {
        isVisible.value = true;
    }

    function hide() {
        isVisible.value = false;
    }

    function reset() {
        localIdentifier.value = '';
        localTexts.value = {};
    }

    function initializeForLanguages(selectedLanguages: string[]) {
        // Initialize text inputs for selected languages
        const newTexts: Record<string, string> = {};
        for (const langCode of selectedLanguages) {
            newTexts[langCode] = localTexts.value[langCode] || '';
        }
        localTexts.value = newTexts;
    }

    function syncWithLanguages(selectedLanguages: string[]) {
        // Synchronize texts with selected languages, preserving existing content
        const newTexts: Record<string, string> = {};
        for (const langCode of selectedLanguages) {
            // Keep existing text if it exists, otherwise initialize as empty
            newTexts[langCode] = langCode in localTexts.value ? localTexts.value[langCode] : '';
        }
        localTexts.value = newTexts;
    }

    function getFormData() {
        return {
            identifier: localIdentifier.value.trim(),
            texts: { ...localTexts.value }
        };
    }

    function isFormValid(
        validateIdentifier: (identifier: string) => { valid: boolean; message: string },
        selectedLanguages: string[]
    ): boolean {
        const identifier = localIdentifier.value.trim();
        return Boolean(identifier) && 
               validateIdentifier(identifier).valid && 
               selectedLanguages.length > 0;
    }

    // Watchers for external visibility control
    function setupVisibilityWatcher(
        visibleProp: () => boolean
    ) {
        return watch(visibleProp, (newVisible) => {
            isVisible.value = newVisible;
            if (newVisible) {
                reset();
            }
        });
    }

    function setupLanguagesWatcher(
        languagesProp: () => string[]
    ) {
        return watch(languagesProp, (newLanguages) => {
            syncWithLanguages(newLanguages);
        });
    }

    return {
        // State
        localIdentifier,
        localTexts,
        isVisible,

        // Methods
        show,
        hide,
        reset,
        initializeForLanguages,
        syncWithLanguages,
        getFormData,
        isFormValid,

        // Watchers setup
        setupVisibilityWatcher,
        setupLanguagesWatcher
    };
}